// file: src/components/layout/AppLayout.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import TopBar from './TopBar';
import LeftSidebar from './LeftSidebar';
import MainContent from './MainContent';
import RightChatPanel from './RightChatPanel';

import CreateProjectModal from '../modals/CreateProjectModal';
import ConnectDataSourcesModal from '../modals/ConnectDataSourcesModal';
import DialogHistoryModal from '../modals/DialogHistoryModal';
import NotificationModal from '../modals/NotificationModal';
import ReparseConfirmModal from '../modals/ReparseConfirmModal';

import {
  getJsonLocalForage,
  setJsonLocalForage,
  clearFileDataCache
} from '../../utils/localForageUtils';
import { useProjectContext } from '../../context/ProjectContext';
import { useChatContext } from '../../context/ChatContext';
import { useViewerContext } from '../../context/ViewerContext';

// Helper function to detect mobile screen size
function isMobileScreen() {
  return window.innerWidth < 768;
}

// Helper function to detect tablet screen size  
function isTabletScreen() {
  return window.innerWidth >= 768 && window.innerWidth < 1024;
}

export default function AppLayout() {
  // Get projects and related functions from context
  const { 
    projects, 
    loading: projectsLoading,
    refreshPhaseFiles,
    refreshProjects,
    deleteDocumentInProject, 
    renameDocumentInProject,
    deleteProject,
    setActiveProjectId,
    activeProjectId,
    runDocumentsInProject
  } = useProjectContext();

  // Get chat state and setters from ChatContext
  const { 
    dialogId, 
    findOrCreateDialogForProject,
    loadDialog,
    refreshDialogList
  } = useChatContext();

  // Get viewer state and functions from ViewerContext
  const { 
    openFiles, 
    setOpenFiles,
    activeFileId, 
    setActiveFileId,
    openFile,
    closeFile,
    isInitialized
  } = useViewerContext();

  // Mobile-aware collapsed states
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [chatPanelCollapsed, setChatPanelCollapsed] = useState(isMobileScreen()); // Collapsed by default on mobile
  
  // Mobile drawer states - only active on mobile screens (< 768px)
  const [sidebarDrawerOpen, setSidebarDrawerOpen] = useState(false);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  
  // Dialog creation state
  const [isCreatingDialog, setIsCreatingDialog] = useState(false);

  // Show modals
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showNoProjectModal, setShowNoProjectModal] = useState(false);
  const [dialogErrorMessage, setDialogErrorMessage] = useState('');
  
  // Upload modal state
  const [uploadingProjectId, setUploadingProjectId] = useState(null);
  const [uploadingPhaseId, setUploadingPhaseId] = useState(null);


  // Chat history modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [currentDialogId, setCurrentDialogId] = useState(null);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('project');
  const fileIdFromUrl = searchParams.get('file');

  // Handle window resize for responsive behavior
  useEffect(() => {
    function handleResize() {
      const isNowMobile = isMobileScreen();
      // If switching to mobile and chat is open, collapse it
      if (isNowMobile && !chatPanelCollapsed) {
        setChatPanelCollapsed(true);
      }
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [chatPanelCollapsed]);

  // Auto-open file from URL parameter or localStorage (from landing page search)
  useEffect(() => {
    if (!projects || projects.length === 0 || !projectIdFromUrl || !isInitialized) return;
    
    // Check for file ID in URL or localStorage
    const targetFileId = fileIdFromUrl || localStorage.getItem('selectedFileId');
    if (!targetFileId) return;
    
    // Find the project
    const project = projects.find(p => String(p.id) === String(projectIdFromUrl));
    if (!project) return;
    
    // Find the file in the project
    let targetFile = null;
    for (const phase of project.phases || []) {
      targetFile = (phase.files || []).find(f => String(f.id) === String(targetFileId));
      if (targetFile) break;
    }
    
    if (targetFile) {
      console.debug('[AppLayout] Auto-opening file from search:', targetFile.name);
      // Check if file is already open
      const alreadyOpen = openFiles.some(f => f.id === targetFile.id);
      if (!alreadyOpen) {
        openFile(targetFile);
      } else {
        // Just set it as active
        setActiveFileId(targetFile.id);
      }
      
      // Clean up - remove from localStorage and URL
      localStorage.removeItem('selectedFileId');
      if (fileIdFromUrl) {
        // Remove file parameter from URL
        searchParams.delete('file');
        navigate(`/projects?${searchParams.toString()}`, { replace: true });
      }
    }
  }, [projectIdFromUrl, fileIdFromUrl, projects, isInitialized, openFile, openFiles, setActiveFileId]);

  // Update UI layout based on project existence, but DON'T clear chat state
  useEffect(() => {
    if (!projects || projects.length === 0) {
      // Only collapse the sidebar when no projects exist AND we're not creating a project
      if (!isCreatingDialog) {
        setSidebarCollapsed(true);
      }
    }
  }, [projects, isCreatingDialog]);

  // Update currentDialogId whenever the context's dialogId changes
  useEffect(() => {
    if (dialogId) {
      setCurrentDialogId(dialogId);
    }
  }, [dialogId]);

  // -------------------------------------------
  // Load openFiles & activeFileId from localForage on mount or when project changes
  // Only do this if projects are loaded and the active project exists
  // -------------------------------------------
  useEffect(() => {
    if (!projects || projects.length === 0 || !projectIdFromUrl || !isInitialized) return;
    async function loadOpenFilesState() {
      const found = projects.find(p => String(p.id) === String(projectIdFromUrl));
      if (!found) {
        setOpenFiles([]);
        setActiveFileId(null);
        console.debug('[Cache] Project not found, clearing state');
        return;
      }

      // Gather all files currently in the project (including JupyterLab virtual files)
      const allProjectFiles = [];
      found.phases.forEach(phase => {
        if (phase.files) {
          allProjectFiles.push(...phase.files);
        }
      });

      // Only keep files that are still present in the project (including JupyterLab files)
      const validCachedFiles = openFiles.filter(cachedFile => {
        return allProjectFiles.some(projectFile => projectFile.id === cachedFile.id);
      });

      // Update cache with cleaned files if any were removed
      if (validCachedFiles.length !== openFiles.length) {
        console.debug(`[Cache] Cleaned cache: ${openFiles.length} -> ${validCachedFiles.length} files`);
        setOpenFiles(validCachedFiles);
      }

      // Validate activeFileId still exists in valid files
      if (activeFileId && !validCachedFiles.some(f => f.id === activeFileId)) {
        const newActiveId = validCachedFiles.length > 0 ? validCachedFiles[0].id : null;
        console.debug(`[Cache] Active file no longer exists, setting to: ${newActiveId}`);
        setActiveFileId(newActiveId);
      }
    }
    loadOpenFilesState();
  }, [projectIdFromUrl, projects, isInitialized, openFiles, activeFileId]);

  // Set active project from URL if present
  useEffect(() => {
    if (projectIdFromUrl && projects && projects.length > 0) {
      const found = projects.find(p => String(p.id) === String(projectIdFromUrl));
      if (found) {
        setActiveProjectId(projectIdFromUrl);
      }
    }
  }, [projectIdFromUrl, projects]);

  // Expand both sidebars when a new project is opened (respect mobile behavior)
  useEffect(() => {
    if (projects && projects.length > 0 && activeProjectId) {
      setSidebarCollapsed(false);
      // Only expand chat panel on desktop/tablet, keep collapsed on mobile
      if (!isMobileScreen()) {
        setChatPanelCollapsed(false);
      }
    }
  }, [activeProjectId]);

  // Refresh projects and KBs/docs whenever the active project changes
  useEffect(() => {
    if (activeProjectId) {
      refreshProjects();
    }
  }, [activeProjectId]);

  // Flush open files and active file when the project changes
  useEffect(() => {
    setOpenFiles([]);
    setActiveFileId(null);
  }, [projectIdFromUrl]);

  // Refresh projects list and handle dialog creation AFTER creation
  async function onProjectCreated(projectId) {
    setShowCreateProject(false);
    
    // Prevent multiple simultaneous dialog creations
    if (isCreatingDialog) {
      console.log(`[AppLayout] Already creating a dialog, ignoring request for project ${projectId}`);
      return;
    }
    
    setIsCreatingDialog(true);
    
    try {
      console.log(`[AppLayout] Starting dialog creation flow for new project ${projectId}`);
      
      // 1. Update project list first
      if (refreshProjects) {
        console.log(`[AppLayout] Refreshing projects list`);
        await refreshProjects();
      }
      
      // 2. Get fresh projects list after refresh
      const projectExists = projects.some(p => p.id === projectId);
      
      if (!projectExists) {
        console.log(`[AppLayout] Project ${projectId} not yet in projects array, waiting for refresh...`);
        // Wait a bit longer for state to update (could be up to next render cycle)
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // 3. Set the active project
      console.log(`[AppLayout] Setting active project to ${projectId}`);
      setActiveProjectId(projectId);
      
      // 4. Refresh the dialog list first to ensure dialogs array is populated
      console.log(`[AppLayout] Refreshing dialog list before creating project dialog`);
      await refreshDialogList(false);
      
      // 5. Create/find dialog for the new project
      console.log(`[AppLayout] Finding or creating dialog for new project ${projectId}...`);
      const result = await findOrCreateDialogForProject(projectId);
      
      if (result && result.dialogId && result.conversationId) {
        console.log(`[AppLayout] Successfully got Dialog ${result.dialogId}, Conv ${result.conversationId} for project ${projectId}`);
      } else {
        console.error(`[AppLayout] findOrCreateDialogForProject did not return valid IDs for project ${projectId}`);
        setDialogErrorMessage("Failed to initialize chat for the new project.");
      }
    } catch (err) {
      console.error(`[AppLayout] Error in project/dialog creation flow for ${projectId}:`, err);
      setDialogErrorMessage("Failed to create chat dialog for new project. Please try again.");
    } finally {
      setIsCreatingDialog(false);
    }
  }

  function handleCollapseSidebar() {
    const isMobile = isMobileScreen();
    const isTablet = isTabletScreen();
    
    if (isMobile && !isTablet) {
      // On mobile only: toggle the drawer
      const newDrawerState = !sidebarDrawerOpen;
      setSidebarDrawerOpen(newDrawerState);
      
      // Close chat drawer when opening sidebar drawer
      if (newDrawerState && chatDrawerOpen) {
        setChatDrawerOpen(false);
      }
    } else {
      // On desktop/tablet: use the old collapse logic
    setSidebarCollapsed(!sidebarCollapsed);
    }
  }

  function handleToggleChatPanel() {
    const isMobile = isMobileScreen();
    const isTablet = isTabletScreen();
    
    if (isMobile && !isTablet) {
      // On mobile only: toggle the drawer
      const newDrawerState = !chatDrawerOpen;
      setChatDrawerOpen(newDrawerState);
      
      // Close sidebar drawer when opening chat drawer
      if (newDrawerState && sidebarDrawerOpen) {
        setSidebarDrawerOpen(false);
      }
    } else {
      // On desktop/tablet: use the old collapse logic
    setChatPanelCollapsed(!chatPanelCollapsed);
    }
  }

  // Called by LeftSidebar when user clicks a file => open it
  function handleFileOpen(file) {
    // Use the ViewerContext's openFile function to ensure proper state management
    openFile(file);
    
    // Close sidebar drawer on mobile after opening a file
    if (isMobile && sidebarDrawerOpen) {
      setSidebarDrawerOpen(false);
    }
  }

  // Called by MainContent when user closes a tab
  function handleTabClose(fileId) {
    // Use the ViewerContext's closeFile function to ensure proper state management
    closeFile(fileId);
  }

  // Use context helpers for file operations
  // const handleFileOpen = openFile;
  // const handleTabClose = closeFile;

  // Called by LeftSidebar when user renames a file
  async function handleFileRename(docId, newName) {
    try {
      await renameDocumentInProject(docId, newName);
      
      // Update open files to reflect the rename
      setOpenFiles(prev => prev.map(f => 
        f.id === docId ? { ...f, name: newName } : f
      ));
    } catch (err) {
      console.error('Error renaming file:', err);
      throw err; // Let the sidebar handle the error display
    }
  }

  // Called by LeftSidebar when user deletes a file
  async function handleFileDelete(doc) {
    try {
      await deleteDocumentInProject(doc.id, doc.kb_id);
      // Clear the file data cache so the viewer doesn't show cached data
      await clearFileDataCache(doc.id);
      // Remove from open files if it was open (use functional update)
      setOpenFiles(prevOpenFiles => {
        let remainingFiles;
        if (doc.type === 'jupyterlab') {
          // Remove both the DB id and the synthetic JupyterLab tab id
          const serverName = doc.server_name || (doc.location ? doc.location.split('/').pop() : undefined);
          remainingFiles = prevOpenFiles.filter(f => f.id !== doc.id && f.id !== `jupyterlab-${serverName}`);
        } else {
          remainingFiles = prevOpenFiles.filter(f => f.id !== doc.id);
        }
        // If the deleted file was active, clear active file or set to another open file
        setActiveFileId(prevActiveId => {
          if (prevActiveId === doc.id) {
            return remainingFiles.length > 0 ? remainingFiles[0].id : null;
          }
          if (doc.type === 'jupyterlab') {
            const serverName = doc.server_name || (doc.location ? doc.location.split('/').pop() : undefined);
            if (prevActiveId === `jupyterlab-${serverName}`) {
              return remainingFiles.length > 0 ? remainingFiles[0].id : null;
            }
          }
          return prevActiveId;
        });
        return remainingFiles;
      });
    } catch (err) {
      console.error('Error deleting file:', err);
      throw err; // Let the sidebar handle the error display
    }
  }

  // Called by LeftSidebar when user clicks upload for a phase
  function handlePhaseUpload(projectId, phaseId) {
    setUploadingProjectId(projectId);
    setUploadingPhaseId(phaseId);
    setShowUploadModal(true);
  }

  // Called when files are successfully uploaded
  async function handleFilesUploaded(uploadedFiles) {
    console.log(`[AppLayout] Files uploaded successfully:`, uploadedFiles);
    
    setShowUploadModal(false);
    const projectId = uploadingProjectId;
    const phaseId = uploadingPhaseId;
    setUploadingProjectId(null);
    setUploadingPhaseId(null);

    // Refresh the phase files to show the newly uploaded files
    if (projectId && phaseId) {
      console.log(`[AppLayout] Refreshing phase files for project ${projectId}, phase ${phaseId}`);
      try {
        await refreshPhaseFiles(projectId, phaseId);
        console.log(`[AppLayout] Phase files refreshed successfully`);
      } catch (err) {
        console.error(`[AppLayout] Failed to refresh phase files:`, err);
      }
    } else {
      console.warn(`[AppLayout] Cannot refresh phase files - missing project ID (${projectId}) or phase ID (${phaseId})`);
    }
  }

  // Helper function to get KB ID for uploading phase
  function getKbIdForUploadingPhase() {
    const proj = projects.find((p) => p.id === uploadingProjectId);
    if (!proj) return null;
    
    const ph = proj.phases.find((x) => x.id === uploadingPhaseId);
    if (!ph) return null;
    
    return ph?.kb_id || null;
  }

  // Handle dialog selection from history
  async function handleDialogChosen({ dialogId: chosenId, conversationId: cId }) {
    try {
      await loadDialog(chosenId, cId);
      
      // Get dialog details to find associated project
      const dialogDetails = await getJsonLocalForage(`dialogDetails-${chosenId}`);
      if (dialogDetails && dialogDetails.project_id) {
        // Set the active project based on the dialog's project_id
        setActiveProjectId(dialogDetails.project_id);
      }
      
      setShowHistoryModal(false);
    } catch (error) {
      console.error("Error loading dialog:", error);
      setDialogErrorMessage("Failed to load the selected conversation. Please try again.");
    }
  }

  // Handle drag start - close the history modal
  function handleChatPanelDragStart() {
    if (showHistoryModal) {
      setShowHistoryModal(false);
    }
  }

  // Function to handle project deletion - UI cleanup BEFORE backend call
  async function handleProjectDelete(projectId) {
    const projectToDeleteDetails = projects.find(p => p.id === projectId);
    
    if (!projectToDeleteDetails) {
      console.error(`Project ${projectId} not found in state.`);
      alert("Error: Could not find project details to clean up UI.");
      return;
    }

    // Check if we're deleting the currently viewed project
    const isDeletingCurrentProject = String(projectId) === String(projectIdFromUrl);

    // UI Cleanup Steps (Tabs, Active File)
    const projectKbIds = projectToDeleteDetails.phases.map(phase => phase.kb_id).filter(Boolean);
    let newActiveFileId = activeFileId;
    const filesToKeep = openFiles.filter(file => {
      const shouldKeep = !file.kb_id || !projectKbIds.includes(file.kb_id);
      if (!shouldKeep && file.id === activeFileId) {
        newActiveFileId = null;
      }
      return shouldKeep;
    });
    
    // Clear cache for all files being removed
    const filesToRemove = openFiles.filter(file => 
      file.kb_id && projectKbIds.includes(file.kb_id)
    );
    for (const file of filesToRemove) {
      await clearFileDataCache(file.id);
    }
    
    if (newActiveFileId === null) {
      newActiveFileId = filesToKeep.length > 0 ? filesToKeep[0].id : null;
    }
    setOpenFiles(filesToKeep);
    setActiveFileId(newActiveFileId);
    
    try {
      // Call backend delete
      await deleteProject(projectId); 
      
      // Remove cached open files and active file for the deleted project
      await setJsonLocalForage(`openFiles-${projectId}`, []);
      await setJsonLocalForage(`activeFileId-${projectId}`, null);
      
      // If we're deleting the currently viewed project, redirect to landing page
      if (isDeletingCurrentProject) {
        navigate('/landing', { replace: true });
        return; // Exit early to avoid setting a new active project
      }
      
      // Set a new active project if the deleted one was active
      if (projects.length > 1) {
        const remainingProjects = projects.filter(p => p.id !== projectId);
        if (remainingProjects.length > 0) {
          setActiveProjectId(remainingProjects[0].id);
        }
      }
    } catch (error) {
      console.error(`Error deleting project ${projectId}:`, error);
      alert(`Failed to delete project: ${error.message}`);
    }
  }

  const [showReparseModal, setShowReparseModal] = useState(false);
  const [reparseDoc, setReparseDoc] = useState(null);

  // Determine if we're on mobile (not tablet)
  const [isMobile, setIsMobile] = useState(() => {
    const mobile = isMobileScreen();
    const tablet = isTabletScreen();
    return mobile && !tablet;
  });
  
  // Update isMobile state on window resize
  useEffect(() => {
    function handleResize() {
      const nowMobile = isMobileScreen();
      const nowTablet = isTabletScreen();
      setIsMobile(nowMobile && !nowTablet);
      
      // Reset drawer states when switching to/from mobile
      if (!nowMobile || nowTablet) {
        setSidebarDrawerOpen(false);
        setChatDrawerOpen(false);
      }
    }
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="app-layout">
      {/* Dot grid background */}
      <div className="fixed inset-0 -z-10 opacity-50 dark:opacity-50 light:opacity-35">
        <div className="absolute inset-0 dark:opacity-100 light:opacity-100 dark:block light:hidden" style={{
          backgroundImage: `
            radial-gradient(circle at 1px 1px, 
              rgba(0, 255, 255, 0.3) 1px, 
              transparent 0
            )
          `,
          backgroundSize: '24px 24px'
        }} />
        <div className="absolute inset-0 dark:hidden light:block" style={{
          backgroundImage: `
            radial-gradient(circle at 1px 1px, 
              rgba(59, 130, 246, 0.3) 1px, 
              transparent 0
            )
          `,
          backgroundSize: '24px 24px'
        }} />
      </div>

      <TopBar
        onCollapseSidebar={handleCollapseSidebar}
        onToggleChatPanel={handleToggleChatPanel}
        projects={projects}
        onFileOpen={handleFileOpen}
        chatPanelCollapsed={chatPanelCollapsed}
      />

      {/* Mobile overlay - only shows when a drawer is open */}
      {isMobile && (sidebarDrawerOpen || chatDrawerOpen) && (
        <div 
          className="mobile-drawer-overlay"
          onClick={() => {
            setSidebarDrawerOpen(false);
            setChatDrawerOpen(false);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998,
          }}
        />
      )}

      <div className="projects-container">
        {/* Left Sidebar - Desktop/Tablet: conditional, Mobile: drawer */}
        {(isMobile ? sidebarDrawerOpen : !sidebarCollapsed) && (
          <LeftSidebar
            className={isMobile ? 'mobile-drawer mobile-drawer-left' : ''}
            style={isMobile ? {
              position: 'fixed',
              top: 0,
              left: sidebarDrawerOpen ? 0 : '-100%',
              bottom: 0,
              width: '85%',
              maxWidth: '320px',
              zIndex: 999,
              transition: 'left 0.3s ease-in-out',
              overflowY: 'auto',
              height: '100vh',
              paddingTop: 0,
            } : {}}
            onFileOpen={handleFileOpen}
            onFileRename={handleFileRename}
            onFileDelete={handleFileDelete}
            onPhaseUpload={handlePhaseUpload}
            onProjectDelete={handleProjectDelete}
            onRequestReparse={(doc) => {
              setReparseDoc(doc);
              setShowReparseModal(true);
            }}
          />
        )}

        <MainContent
          className={isMobile && (sidebarDrawerOpen || chatDrawerOpen) ? 'mobile-hidden' : ''}
          style={isMobile && (sidebarDrawerOpen || chatDrawerOpen) ? { display: 'none' } : {}}
          openFiles={openFiles}
          activeFileId={activeFileId}
          onTabClose={handleTabClose}
          onTabSelect={setActiveFileId}
          sidebarCollapsed={sidebarCollapsed}
          chatPanelCollapsed={chatPanelCollapsed}
          projectsExist={projects && projects.length > 0}
        />

        {/* Right Chat Panel - Desktop/Tablet: conditional, Mobile: drawer */}
        {(isMobile ? chatDrawerOpen : !chatPanelCollapsed) && (
          <RightChatPanel 
            className={isMobile ? 'mobile-drawer mobile-drawer-right' : ''}
            style={isMobile ? {
              position: 'fixed',
              top: 0,
              right: chatDrawerOpen ? 0 : '-100%',
              bottom: 0,
              width: '85%',
              maxWidth: '320px',
              zIndex: 999,
              transition: 'right 0.3s ease-in-out',
              overflowY: 'auto',
              height: '100vh',
              paddingTop: 0,
            } : {}}
            collapsed={chatPanelCollapsed}
            onHistoryClick={() => setShowHistoryModal(true)}
            onDragStart={handleChatPanelDragStart}
          />
        )}
      </div>

      {showCreateProject && (
        <CreateProjectModal
          onClose={() => setShowCreateProject(false)}
          onProjectCreated={onProjectCreated}
        />
      )}

      {showUploadModal && (
        <ConnectDataSourcesModal
          onClose={() => setShowUploadModal(false)}
          onFilesUploaded={handleFilesUploaded}
          kbId={getKbIdForUploadingPhase()}
          getKbId={getKbIdForUploadingPhase}
        />
      )}

      {showHistoryModal && (
        <DialogHistoryModal
          onClose={() => setShowHistoryModal(false)}
          onDialogChosen={handleDialogChosen}
          currentDialogId={currentDialogId}
        />
      )}

      {showNoProjectModal && (
        <NotificationModal
          title="No Projects"
          message="Please create a project first before starting a chat dialog."
          onClose={() => setShowNoProjectModal(false)}
        />
      )}

      {dialogErrorMessage && (
        <NotificationModal
          title="Error"
          message={dialogErrorMessage}
          onClose={() => setDialogErrorMessage('')}
        />
      )}

      {showReparseModal && reparseDoc && (
        <ReparseConfirmModal
          docName={reparseDoc.name}
          onClose={() => setShowReparseModal(false)}
          onConfirm={() => {
            if (reparseDoc && reparseDoc.id) {
              runDocumentsInProject(reparseDoc.id, { deleteExisting: true });
            }
            setShowReparseModal(false);
          }}
        />
      )}
    </div>
  );
}

